import { inject, Injectable, Signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import {
    ApiService,
    BusinessModel,
    Customer,
    Order,
    OrderShop,
    OrderType,
    PaymentType,
    Table
} from '@us/web/shared/data-access';
import { parseDataServer, validateDeliveryAddress } from '@us/web/shared/utils';
import { isNil } from 'lodash';
import { DeviceDetectorService } from 'ngx-device-detector';
import { map, Observable } from 'rxjs';
import { Cart, CartFillable, CartItem, CartMember } from '../+state/cart.reducer';
import { ENVIRONMENT } from '@us/app-config';
import { selectAllowShopDelivery, selectTurnOffAddress } from '@us/web/shop/data-access';
import { Store } from '@ngrx/store';

@Injectable({
    providedIn: 'root'
})
export class CartApiService {
    private readonly apiService = inject(ApiService);
    private readonly deviceService = inject(DeviceDetectorService);
    private readonly translocoService = inject(TranslocoService);
    private readonly env = inject(ENVIRONMENT);
    private readonly store = inject(Store);
    protected allowShopDelivery: Signal<boolean> = this.store.selectSignal(selectAllowShopDelivery);
    protected turnOffAddress: Signal<boolean> = this.store.selectSignal(selectTurnOffAddress);

    createCart(data: Cart) {
        return this.apiService.post('carts', data).pipe(map(e => parseDataServer(e)));
    }

    getCart(id: string) {
        return this.apiService.get(`carts/${id}`).pipe(map(e => parseDataServer(e)));
    }

    updateCart(id: string, data: CartFillable) {
        return this.apiService.patch(`carts/${id}`, data).pipe(map(e => parseDataServer(e)));
    }

    addItemToCart(
        id: string,
        data: {
            item: CartItem;
            shop?: OrderShop;
            order_type?: OrderType;
            table?: Table;
            customer?: Customer;
        }
    ) {
        const params = {
            ...data,
            item: {
                ...data.item,
                options: data.item.options?.map(o => {
                    return {
                        group_id: o.group_id,
                        id: String(o.id),
                        name: o.name,
                        price: o.price,
                        quantity: o.quantity
                    };
                })
            }
        };
        return this.apiService.post(`carts/${id}/items`, params).pipe(map(e => parseDataServer(e)));
    }

    updateItem(
        id: string,
        itemId: string,
        {
            quantity
        }: {
            quantity: number;
        }
    ) {
        return this.apiService.patch(`carts/${id}/items/${itemId}`, { quantity }).pipe(map(e => parseDataServer(e)));
    }

    removeItemFromCart(id: string, itemId: string) {
        return this.apiService.delete(`carts/${id}/items/${itemId}`).pipe(map(e => parseDataServer(e)));
    }

    joinSharedOrder(cartId: string, user: CartMember) {
        return this.apiService.post(`carts/${cartId}/join`, { ...user }).pipe(map(e => parseDataServer(e)));
    }

    removeCartMember(cartId: string, userId: string) {
        return this.apiService.delete(`carts/${cartId}/members/${userId}`).pipe(map(e => parseDataServer(e)));
    }

    submitCart(
        cartId: string,
        data: {
            business_model?: BusinessModel | null;
            payment_type?: PaymentType | null;
        }
    ): Observable<Cart> {
        return this.apiService.post(`carts/${cartId}/submit`, data).pipe(map(e => parseDataServer(e)));
    }

    public validateOrder(order: Cart) {
        // if (!this.shopService.inServiceTime()) {
        //     throw new Error(this.translocoService.translate('the_store_has_run_out_of_service_time'));
        // }

        if (isNil(order.order_type)) {
            throw new Error(this.translocoService.translate('please_select_the_form_of_delivery'));
        }

        if (order.order_type === OrderType.Delivery) {
            const hidden_address = this.allowShopDelivery() && this.turnOffAddress();
            if (!order.receiver || (!validateDeliveryAddress(order.receiver, order.order_type) && !hidden_address)) {
                throw new Error(this.translocoService.translate('please_fill_in_the_delivery_information'));
            }
        }

        if (order.order_type === OrderType.Delivery) {
            if (isNil(order.delivery) && this.env.appType !== 'retail') {
                throw new Error(this.translocoService.translate('please_select_a_delivery_partner'));
            }
        }

        if (order.order_type === OrderType.OrderAtTheTable) {
            if (isNil(order.order_table)) {
                throw new Error(this.translocoService.translate('please_select_a_table'));
            }
        }
    }

    selectTable(cartId: string, params: { table_id: number, user?: any }){
        return this.apiService.post<Order>(`carts/${cartId}/table`, params).pipe(map(e => parseDataServer(e)));
    }

    validateTable(params: { tableId: number; shopId?: string }) {
        return this.apiService.post<Table>(`validate-table`, params);
    }
}
